import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AcceptTermsData,
  AcceptTermsResponse,
  AdminAddInvestorCreditsData,
  AdminAddInvestorCreditsResponse,
  AdminAddStartupCreditsData,
  AdminAddStartupCreditsResponse,
  AdminDeleteEvaluationData,
  AdminDeleteEvaluationResponse,
  AdminDeletePitchSessionData,
  AdminDeletePitchSessionResponse,
  AdminDeletePrescreenData,
  AdminDeletePrescreenResponse,
  AdminDeleteUserData,
  AdminDeleteUserResponse,
  AdminGetEligibleStartupsData,
  AdminListAutomatedEmailsData,
  AdminListDataRoomsData,
  AdminListEvaluationsData,
  AdminListFeedbackData,
  AdminListInvestorsData,
  AdminListOnboardingData,
  AdminListPrescreensData,
  AdminListStartupsData,
  AdminListUsersData,
  AdminRegenerateEvaluationData,
  AdminRegenerateEvaluationResponse,
  AdminRerunPrescreenData,
  AdminRerunPrescreenResponse,
  AdminTriggerPersonaResearchData,
  AdminTriggerPersonaResearchResponse,
  AdminTriggerPrescreenData,
  AdminTriggerPrescreenResponse,
  CompleteOnboardingData,
  CompleteOnboardingResponse,
  ConnectWithStartupData,
  ConnectWithStartupResponse,
  CreateDocumentData,
  CreateDocumentResponse,
  CreateInvestorData,
  CreateInvestorResponse,
  CreatePitchData,
  CreatePitchResponse,
  CreateStartupData,
  CreateStartupDataRoomData,
  CreateStartupDataRoomResponse,
  CreateStartupMemberData,
  CreateStartupMemberResponse,
  CreateStartupResponse,
  DeleteStartupMemberData,
  DeleteStartupMemberResponse,
  FinishPitchData,
  FinishPitchResponse,
  GetDataRoomDocumentDownloadData,
  GetDataRoomDocumentDownloadResponse,
  GetDataRoomVideoUrlData,
  GetDataRoomVideoUrlResponse,
  GetDocumentDownloadUrlData,
  GetDocumentDownloadUrlResponse,
  GetInvestorPitchDeckDownloadData,
  GetInvestorPitchDeckDownloadResponse,
  GetInvestorPitchDocumentDownloadData,
  GetInvestorPitchDocumentDownloadResponse,
  GetInvestorPitchVideoUrlData,
  GetInvestorPitchVideoUrlResponse,
  GetPitchDeckDownloadData,
  GetPitchDeckDownloadResponse,
  GetPitchVideoUrlData,
  GetPitchVideoUrlResponse,
  InvestorConnectWithStartupData,
  InvestorConnectWithStartupResponse,
  LinkFounderResumeData,
  LinkFounderResumeResponse,
  LinkStartupDocumentData,
  LinkStartupDocumentResponse,
  Options,
  RecordDataRoomViewData,
  RecordDataRoomViewResponse,
  RequestInvestorEvaluationData,
  RequestInvestorEvaluationResponse,
  RevokeStartupDataRoomData,
  RevokeStartupDataRoomResponse,
  SetOnboardingRoleData,
  SetOnboardingRoleResponse,
  StartPitchData,
  StartPitchResponse,
  UnlinkFounderResumeData,
  UnlinkStartupDocumentData,
  UnlinkStartupDocumentResponse,
  UpdateInvestorData,
  UpdateInvestorResponse,
  UpdateMeData,
  UpdateMeResponse,
  UpdatePitchData,
  UpdatePitchResponse,
  UpdateStartupData,
  UpdateStartupDocumentData,
  UpdateStartupDocumentResponse,
  UpdateStartupMemberData,
  UpdateStartupMemberResponse,
  UpdateStartupResponse,
  VerifyDocumentData,
  VerifyDocumentResponse,
} from '@workspace/api-backend'
import {
  acceptTermsMutation,
  adminAddInvestorCreditsMutation,
  adminAddStartupCreditsMutation,
  adminDeleteEvaluationMutation,
  adminDeletePitchSessionMutation,
  adminDeletePrescreenMutation,
  adminDeleteUserMutation,
  adminGetAutomatedEmailByIdOptions,
  adminGetDataRoomAnalyticsOptions,
  adminGetDataRoomByIdOptions,
  adminGetEligibleStartupsOptions,
  adminGetEvaluationByIdOptions,
  adminGetEvaluationByIdQueryKey,
  adminGetInvestorByIdOptions,
  adminGetInvestorPersonaOptions,
  adminGetInvestorPersonaQueryKey,
  adminGetOnboardingByIdOptions,
  adminGetPrescreenByIdOptions,
  adminGetPrescreenByIdQueryKey,
  adminGetPrescreenDocumentsOptions,
  adminGetStartupByIdOptions,
  adminGetStartupDocumentsOptions,
  adminGetUserByIdOptions,
  adminGetUserDeletionPreviewOptions,
  adminListAutomatedEmailsOptions,
  adminListDataRoomsOptions,
  adminListEvaluationsOptions,
  adminListEvaluationsQueryKey,
  adminListFeedbackOptions,
  adminListInvestorCreditsOptions,
  adminListInvestorCreditsQueryKey,
  adminListInvestorsOptions,
  adminListOnboardingOptions,
  adminListPrescreensOptions,
  adminListPrescreensQueryKey,
  adminListStartupCreditsOptions,
  adminListStartupCreditsQueryKey,
  adminListStartupsOptions,
  adminListUsersOptions,
  adminListUsersQueryKey,
  adminRegenerateEvaluationMutation,
  adminRerunPrescreenMutation,
  adminTriggerPersonaResearchMutation,
  adminTriggerPrescreenMutation,
  completeOnboardingMutation,
  connectWithStartupMutation,
  createDocumentMutation,
  createInvestorMutation,
  createPitchMutation,
  createStartupDataRoomMutation,
  createStartupMemberMutation,
  createStartupMutation,
  deleteStartupMemberMutation,
  finishPitchMutation,
  getDataRoomAnalyticsOptions,
  getDataRoomContentOptions,
  getDataRoomContentQueryKey,
  getDataRoomDocumentDownloadMutation,
  getDataRoomVideoUrlMutation,
  getDocumentDownloadUrlMutation,
  getInvestorByIdOptions,
  getInvestorConfigOptions,
  getInvestorCreditBalanceOptions,
  getInvestorOptions,
  getInvestorPitchDeckDownloadMutation,
  getInvestorPitchDetailOptions,
  getInvestorPitchDetailQueryKey,
  getInvestorPitchDocumentDownloadMutation,
  getInvestorPitchVideoUrlMutation,
  getInvestorQueryKey,
  getMeOptions,
  getMeQueryKey,
  getOnboardingStatusQueryKey,
  getPitchDeckDownloadMutation,
  getPitchOptions,
  getPitchQueryKey,
  getPitchVideoUrlMutation,
  getPublicInvestorBySlugOptions,
  getStartupConfigOptions,
  getStartupCreditBalanceOptions,
  getStartupDataRoomOptions,
  getStartupDataRoomQueryKey,
  getStartupOptions,
  getStartupQueryKey,
  getUserByIdOptions,
  investorConnectWithStartupMutation,
  linkFounderResumeMutation,
  linkStartupDocumentMutation,
  listInvestorCreditsOptions,
  listPitchesOptions,
  listPitchesQueryKey,
  listStartupCreditsOptions,
  listStartupDocumentsOptions,
  listStartupDocumentsQueryKey,
  listStartupMembersOptions,
  listStartupMembersQueryKey,
  recordDataRoomViewMutation,
  requestInvestorEvaluationMutation,
  revokeStartupDataRoomMutation,
  setOnboardingRoleMutation,
  startPitchMutation,
  unlinkFounderResumeMutation,
  unlinkStartupDocumentMutation,
  updateInvestorMutation,
  updateMeMutation,
  updatePitchMutation,
  updateStartupDocumentMutation,
  updateStartupMemberMutation,
  updateStartupMutation,
  verifyDocumentMutation,
} from '@workspace/api-backend/react-query'

// ============================================================================
// Query Hooks
// ============================================================================

export function useStartupConfigQuery(
  queryOptions?: Partial<ReturnType<typeof getStartupConfigOptions>>
) {
  return useQuery({
    ...getStartupConfigOptions(),
    ...queryOptions,
  })
}

export function useInvestorConfigQuery(
  queryOptions?: Partial<ReturnType<typeof getInvestorConfigOptions>>
) {
  return useQuery({
    ...getInvestorConfigOptions(),
    ...queryOptions,
  })
}

export function useStartupQuery(queryOptions?: Partial<ReturnType<typeof getStartupOptions>>) {
  return useQuery({
    ...getStartupOptions(),
    ...queryOptions,
  })
}

export function useCurrentUserQuery(queryOptions?: Partial<ReturnType<typeof getMeOptions>>) {
  return useQuery({
    ...getMeOptions(),
    ...queryOptions,
  })
}

export function useUserByIdQuery(
  userId: string,
  queryOptions?: Partial<ReturnType<typeof getUserByIdOptions>>
) {
  return useQuery({
    ...getUserByIdOptions({ path: { id: userId } }),
    ...queryOptions,
  })
}

export function useInvestorByIdQuery(
  investorId: string,
  queryOptions?: Partial<ReturnType<typeof getInvestorByIdOptions>>
) {
  return useQuery({
    ...getInvestorByIdOptions({ path: { id: investorId } }),
    ...queryOptions,
  })
}

// Admin: List startups
export function useAdminStartupsQuery(
  options?: Options<AdminListStartupsData>,
  queryOptions?: Partial<ReturnType<typeof adminListStartupsOptions>>
) {
  return useQuery({
    ...adminListStartupsOptions(options),
    ...queryOptions,
  })
}

// Admin: Get startup by ID
export function useAdminStartupByIdQuery(
  startupId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetStartupByIdOptions>>
) {
  return useQuery({
    ...adminGetStartupByIdOptions({ path: { id: startupId } }),
    ...queryOptions,
  })
}

// Admin: List users
export function useAdminUsersQuery(
  options?: Options<AdminListUsersData>,
  queryOptions?: Partial<ReturnType<typeof adminListUsersOptions>>
) {
  return useQuery({
    ...adminListUsersOptions(options),
    ...queryOptions,
  })
}

// Admin: Get user by ID
export function useAdminUserByIdQuery(
  userId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetUserByIdOptions>>
) {
  return useQuery({
    ...adminGetUserByIdOptions({ path: { id: userId } }),
    ...queryOptions,
  })
}

// Admin: Get user deletion preview
export function useAdminUserDeletionPreviewQuery(
  userId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetUserDeletionPreviewOptions>>
) {
  return useQuery({
    ...adminGetUserDeletionPreviewOptions({ path: { id: userId } }),
    ...queryOptions,
    enabled: queryOptions?.enabled ?? true,
  })
}

// Admin: Delete user
export function useAdminDeleteUserMutation(
  callbacks?: MutationCallbacks<AdminDeleteUserResponse, Options<AdminDeleteUserData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...adminDeleteUserMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: adminListUsersQueryKey(),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Admin: List investors
export function useAdminInvestorsQuery(
  options?: Options<AdminListInvestorsData>,
  queryOptions?: Partial<ReturnType<typeof adminListInvestorsOptions>>
) {
  return useQuery({
    ...adminListInvestorsOptions(options),
    ...queryOptions,
  })
}

// Admin: Get investor by ID
export function useAdminInvestorByIdQuery(
  investorId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetInvestorByIdOptions>>
) {
  return useQuery({
    ...adminGetInvestorByIdOptions({ path: { id: investorId } }),
    ...queryOptions,
  })
}

// Admin: Get investor persona
export function useAdminInvestorPersonaQuery(
  investorId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetInvestorPersonaOptions>>
) {
  return useQuery({
    ...adminGetInvestorPersonaOptions({ path: { id: investorId } }),
    ...queryOptions,
  })
}

// Admin: Trigger persona research
export function useAdminTriggerPersonaResearchMutation(
  investorId: string,
  callbacks?: MutationCallbacks<
    AdminTriggerPersonaResearchResponse,
    Options<AdminTriggerPersonaResearchData>
  >
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...adminTriggerPersonaResearchMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: adminGetInvestorPersonaQueryKey({ path: { id: investorId } }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Admin: List evaluations
export function useAdminEvaluationsQuery(
  options?: Options<AdminListEvaluationsData>,
  queryOptions?: Partial<ReturnType<typeof adminListEvaluationsOptions>>
) {
  return useQuery({
    ...adminListEvaluationsOptions(options),
    ...queryOptions,
  })
}

// Admin: Get evaluation by ID
export function useAdminEvaluationByIdQuery(
  evaluationId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetEvaluationByIdOptions>>
) {
  return useQuery({
    ...adminGetEvaluationByIdOptions({ path: { id: evaluationId } }),
    ...queryOptions,
  })
}

// Admin: Regenerate evaluation
export function useAdminRegenerateEvaluationMutation(
  callbacks?: MutationCallbacks<
    AdminRegenerateEvaluationResponse,
    Options<AdminRegenerateEvaluationData>
  >
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...adminRegenerateEvaluationMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: adminGetEvaluationByIdQueryKey({ path: { id: variables.path.id } }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Admin: List feedback
export function useAdminFeedbackQuery(
  options?: Options<AdminListFeedbackData>,
  queryOptions?: Partial<ReturnType<typeof adminListFeedbackOptions>>
) {
  return useQuery({
    ...adminListFeedbackOptions(options),
    ...queryOptions,
  })
}

// Admin: List startup credits
export function useAdminStartupCreditsQuery(
  startupId: string,
  queryOptions?: Partial<ReturnType<typeof adminListStartupCreditsOptions>>
) {
  return useQuery({
    ...adminListStartupCreditsOptions({ path: { id: startupId } }),
    ...queryOptions,
  })
}

// Admin: Add startup credits
export function useAdminAddStartupCreditsMutation(
  callbacks?: MutationCallbacks<AdminAddStartupCreditsResponse, Options<AdminAddStartupCreditsData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...adminAddStartupCreditsMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: adminListStartupCreditsQueryKey({ path: variables.path }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Admin: List investor credits
export function useAdminInvestorCreditsQuery(
  investorId: string,
  queryOptions?: Partial<ReturnType<typeof adminListInvestorCreditsOptions>>
) {
  return useQuery({
    ...adminListInvestorCreditsOptions({ path: { id: investorId } }),
    ...queryOptions,
  })
}

// Admin: Add investor credits
export function useAdminAddInvestorCreditsMutation(
  callbacks?: MutationCallbacks<
    AdminAddInvestorCreditsResponse,
    Options<AdminAddInvestorCreditsData>
  >
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...adminAddInvestorCreditsMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: adminListInvestorCreditsQueryKey({ path: variables.path }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Get startup credit balance
export function useStartupCreditBalanceQuery(
  startupId: string | undefined,
  queryOptions?: Partial<ReturnType<typeof getStartupCreditBalanceOptions>>
) {
  return useQuery({
    ...getStartupCreditBalanceOptions({ path: { startupId: startupId ?? '' } }),
    ...queryOptions,
    enabled: !!startupId && (queryOptions?.enabled ?? true),
  })
}

// Get investor credit balance
export function useInvestorCreditBalanceQuery(
  investorId: string | undefined,
  queryOptions?: Partial<ReturnType<typeof getInvestorCreditBalanceOptions>>
) {
  return useQuery({
    ...getInvestorCreditBalanceOptions({ path: { id: investorId ?? '' } }),
    ...queryOptions,
    enabled: !!investorId && (queryOptions?.enabled ?? true),
  })
}

// List startup credit transactions (user-facing)
export function useStartupCreditsQuery(startupId: string | undefined) {
  return useQuery({
    ...listStartupCreditsOptions({ path: { startupId: startupId ?? '' } }),
    enabled: !!startupId,
  })
}

// List investor credit transactions (user-facing)
export function useInvestorCreditsQuery(investorId: string | undefined) {
  return useQuery({
    ...listInvestorCreditsOptions({ path: { id: investorId ?? '' } }),
    enabled: !!investorId,
  })
}

// List startup documents
export function useStartupDocumentsQuery(
  startupId: string | undefined,
  queryOptions?: Partial<ReturnType<typeof listStartupDocumentsOptions>>
) {
  return useQuery({
    ...listStartupDocumentsOptions({ path: { startupId: startupId ?? '' } }),
    ...queryOptions,
    enabled: !!startupId && (queryOptions?.enabled ?? true),
  })
}

// ============================================================================
// Mutation Hooks
// ============================================================================

interface MutationCallbacks<TData, TVariables> {
  onSuccess?: (data: TData, variables: TVariables, context: unknown) => void
  onError?: (error: Error, variables: TVariables, context: unknown) => void
  onSettled?: (
    data: TData | undefined,
    error: Error | null,
    variables: TVariables,
    context: unknown
  ) => void
}

// Set onboarding role
export function useSetOnboardingRoleMutation(
  callbacks?: MutationCallbacks<SetOnboardingRoleResponse, Options<SetOnboardingRoleData>>,
  options?: Partial<Options<SetOnboardingRoleData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...setOnboardingRoleMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getOnboardingStatusQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Update user profile
export function useUpdateMeMutation(
  callbacks?: MutationCallbacks<UpdateMeResponse, Options<UpdateMeData>>,
  options?: Partial<Options<UpdateMeData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...updateMeMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getMeQueryKey() })
      await queryClient.invalidateQueries({ queryKey: getOnboardingStatusQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Accept terms of service
export function useAcceptTermsMutation(
  callbacks?: MutationCallbacks<AcceptTermsResponse, Options<AcceptTermsData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...acceptTermsMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getMeQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Create startup
export function useCreateStartupMutation(
  callbacks?: MutationCallbacks<CreateStartupResponse, Options<CreateStartupData>>,
  options?: Partial<Options<CreateStartupData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...createStartupMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getStartupQueryKey() })
      await queryClient.invalidateQueries({ queryKey: getOnboardingStatusQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Update startup
export function useUpdateStartupMutation(
  callbacks?: MutationCallbacks<UpdateStartupResponse, Options<UpdateStartupData>>,
  options?: Partial<Options<UpdateStartupData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...updateStartupMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getStartupQueryKey() })
      await queryClient.invalidateQueries({ queryKey: getOnboardingStatusQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Complete onboarding
export function useCompleteOnboardingMutation(
  callbacks?: MutationCallbacks<CompleteOnboardingResponse, Options<CompleteOnboardingData>>,
  options?: Partial<Options<CompleteOnboardingData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...completeOnboardingMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getMeQueryKey() })
      await queryClient.invalidateQueries({ queryKey: getOnboardingStatusQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Link document to startup
export function useLinkStartupDocumentMutation(
  callbacks?: MutationCallbacks<LinkStartupDocumentResponse, Options<LinkStartupDocumentData>>,
  options?: Partial<Options<LinkStartupDocumentData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...linkStartupDocumentMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getStartupQueryKey() })
      await queryClient.invalidateQueries({ queryKey: getOnboardingStatusQueryKey() })
      await queryClient.invalidateQueries({
        queryKey: listStartupDocumentsQueryKey({ path: { startupId: variables.path.startupId } }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Unlink document from startup
export function useUnlinkStartupDocumentMutation(
  callbacks?: MutationCallbacks<UnlinkStartupDocumentResponse, Options<UnlinkStartupDocumentData>>,
  options?: Partial<Options<UnlinkStartupDocumentData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...unlinkStartupDocumentMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getStartupQueryKey() })
      await queryClient.invalidateQueries({
        queryKey: listStartupDocumentsQueryKey({ path: { startupId: variables.path.startupId } }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Update startup document label
export function useUpdateStartupDocumentMutation(
  callbacks?: MutationCallbacks<UpdateStartupDocumentResponse, Options<UpdateStartupDocumentData>>,
  options?: Partial<Options<UpdateStartupDocumentData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...updateStartupDocumentMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getStartupQueryKey() })
      await queryClient.invalidateQueries({ queryKey: getOnboardingStatusQueryKey() })
      await queryClient.invalidateQueries({
        queryKey: listStartupDocumentsQueryKey({ path: { startupId: variables.path.startupId } }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Get document download URL
export function useGetDocumentDownloadUrlMutation(
  callbacks?: MutationCallbacks<
    GetDocumentDownloadUrlResponse,
    Options<GetDocumentDownloadUrlData>
  >,
  options?: Partial<Options<GetDocumentDownloadUrlData>>
) {
  return useMutation({
    ...getDocumentDownloadUrlMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Create document (no invalidation needed - used for upload flow)
export function useCreateDocumentMutation(
  callbacks?: MutationCallbacks<CreateDocumentResponse, Options<CreateDocumentData>>,
  options?: Partial<Options<CreateDocumentData>>
) {
  return useMutation({
    ...createDocumentMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Verify document (no invalidation needed - used for upload flow)
export function useVerifyDocumentMutation(
  callbacks?: MutationCallbacks<VerifyDocumentResponse, Options<VerifyDocumentData>>,
  options?: Partial<Options<VerifyDocumentData>>
) {
  return useMutation({
    ...verifyDocumentMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// ============================================================================
// Startup Members Hooks
// ============================================================================

// List startup members
export function useStartupMembersQuery(
  startupId: string | undefined,
  queryOptions?: Partial<ReturnType<typeof listStartupMembersOptions>>
) {
  return useQuery({
    ...listStartupMembersOptions({ path: { startupId: startupId ?? '' } }),
    ...queryOptions,
    enabled: !!startupId && (queryOptions?.enabled ?? true),
  })
}

// Create startup member
export function useCreateStartupMemberMutation(
  callbacks?: MutationCallbacks<CreateStartupMemberResponse, Options<CreateStartupMemberData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...createStartupMemberMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: listStartupMembersQueryKey({ path: variables.path }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Update startup member
export function useUpdateStartupMemberMutation(
  callbacks?: MutationCallbacks<UpdateStartupMemberResponse, Options<UpdateStartupMemberData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...updateStartupMemberMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: listStartupMembersQueryKey({ path: variables.path }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Delete startup member
export function useDeleteStartupMemberMutation(
  callbacks?: MutationCallbacks<DeleteStartupMemberResponse, Options<DeleteStartupMemberData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...deleteStartupMemberMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: listStartupMembersQueryKey({ path: variables.path }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// ============================================================================
// Pitch Hooks
// ============================================================================

// List pitches — pass either startupId or investorId
export function usePitchesQuery(
  filter: { startupId: string } | { investorId: string },
  queryOptions?: Partial<ReturnType<typeof listPitchesOptions>>
) {
  return useQuery({
    ...listPitchesOptions({ query: filter }),
    staleTime: 0,
    refetchOnMount: 'always',
    ...queryOptions,
  })
}

// Get pitch by ID
export function usePitchQuery(
  pitchId: string | undefined,
  queryOptions?: Partial<ReturnType<typeof getPitchOptions>>
) {
  return useQuery({
    ...getPitchOptions({ path: { pitchId: pitchId ?? '' } }),
    ...queryOptions,
    enabled: !!pitchId && (queryOptions?.enabled ?? true),
  })
}

// Create pitch
export function useCreatePitchMutation(
  callbacks?: MutationCallbacks<CreatePitchResponse, Options<CreatePitchData>>,
  options?: Partial<Options<CreatePitchData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...createPitchMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: listPitchesQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Start pitch (creates LiveKit room and returns token)
export function useStartPitchMutation(
  callbacks?: MutationCallbacks<StartPitchResponse, Options<StartPitchData>>,
  options?: Partial<Options<StartPitchData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...startPitchMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: listPitchesQueryKey() })
      await queryClient.invalidateQueries({
        queryKey: getPitchQueryKey({ path: { pitchId: variables.path.pitchId } }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Finish pitch (marks as completed)
export function useFinishPitchMutation(
  callbacks?: MutationCallbacks<FinishPitchResponse, Options<FinishPitchData>>,
  options?: Partial<Options<FinishPitchData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...finishPitchMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: listPitchesQueryKey() })
      await queryClient.invalidateQueries({
        queryKey: getPitchQueryKey({ path: { pitchId: variables.path.pitchId } }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Get pitch video URL (generates presigned S3 URL on demand)
export function useGetPitchVideoUrlMutation(
  callbacks?: MutationCallbacks<GetPitchVideoUrlResponse, Options<GetPitchVideoUrlData>>,
  options?: Partial<Options<GetPitchVideoUrlData>>
) {
  return useMutation({
    ...getPitchVideoUrlMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Update pitch (cover image, publish status)
export function useUpdatePitchMutation(
  pitchId: string,
  callbacks?: MutationCallbacks<UpdatePitchResponse, Options<UpdatePitchData>>,
  options?: Partial<Options<UpdatePitchData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...updatePitchMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: getPitchQueryKey({ path: { pitchId } }),
      })
      await queryClient.invalidateQueries({ queryKey: listPitchesQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// ============================================================================
// Founder Resume Hooks
// ============================================================================

// Link founder resume
export function useLinkFounderResumeMutation(
  callbacks?: MutationCallbacks<LinkFounderResumeResponse, Options<LinkFounderResumeData>>,
  options?: Partial<Options<LinkFounderResumeData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...linkFounderResumeMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getMeQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Unlink founder resume
export function useUnlinkFounderResumeMutation(
  callbacks?: MutationCallbacks<unknown, Options<UnlinkFounderResumeData>>,
  options?: Partial<Options<UnlinkFounderResumeData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...unlinkFounderResumeMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getMeQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// ============================================================================
// Investor Hooks
// ============================================================================

// Get investor
export function useInvestorQuery(queryOptions?: Partial<ReturnType<typeof getInvestorOptions>>) {
  return useQuery({
    ...getInvestorOptions(),
    ...queryOptions,
  })
}

// Create investor
export function useCreateInvestorMutation(
  callbacks?: MutationCallbacks<CreateInvestorResponse, Options<CreateInvestorData>>,
  options?: Partial<Options<CreateInvestorData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...createInvestorMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getInvestorQueryKey() })
      await queryClient.invalidateQueries({ queryKey: getOnboardingStatusQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Update investor
export function useUpdateInvestorMutation(
  callbacks?: MutationCallbacks<UpdateInvestorResponse, Options<UpdateInvestorData>>,
  options?: Partial<Options<UpdateInvestorData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...updateInvestorMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: getInvestorQueryKey() })
      await queryClient.invalidateQueries({ queryKey: getOnboardingStatusQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Get investor pitch detail (same data as data room content, but by pitch ID for investors)
export function useInvestorPitchDetailQuery(
  investorId: string | undefined,
  pitchId: string | undefined,
  queryOptions?: Partial<ReturnType<typeof getInvestorPitchDetailOptions>>
) {
  return useQuery({
    ...getInvestorPitchDetailOptions({ path: { id: investorId ?? '', pitchId: pitchId ?? '' } }),
    ...queryOptions,
    enabled: !!investorId && !!pitchId && (queryOptions?.enabled ?? true),
  })
}

// Get investor pitch document download URL
export function useGetInvestorPitchDocumentDownloadMutation(
  callbacks?: MutationCallbacks<
    GetInvestorPitchDocumentDownloadResponse,
    Options<GetInvestorPitchDocumentDownloadData>
  >,
  options?: Partial<Options<GetInvestorPitchDocumentDownloadData>>
) {
  return useMutation({
    ...getInvestorPitchDocumentDownloadMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Get investor pitch deck download URL
export function useGetInvestorPitchDeckDownloadMutation(
  callbacks?: MutationCallbacks<
    GetInvestorPitchDeckDownloadResponse,
    Options<GetInvestorPitchDeckDownloadData>
  >,
  options?: Partial<Options<GetInvestorPitchDeckDownloadData>>
) {
  return useMutation({
    ...getInvestorPitchDeckDownloadMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Get investor pitch video URL
export function useGetInvestorPitchVideoUrlMutation(
  callbacks?: MutationCallbacks<
    GetInvestorPitchVideoUrlResponse,
    Options<GetInvestorPitchVideoUrlData>
  >,
  options?: Partial<Options<GetInvestorPitchVideoUrlData>>
) {
  return useMutation({
    ...getInvestorPitchVideoUrlMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Connect with startup via investor pitch
export function useInvestorConnectWithStartupMutation(
  callbacks?: MutationCallbacks<
    InvestorConnectWithStartupResponse,
    Options<InvestorConnectWithStartupData>
  >,
  options?: Partial<Options<InvestorConnectWithStartupData>>
) {
  return useMutation({
    ...investorConnectWithStartupMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Request personalized investor evaluation
export function useRequestInvestorEvaluationMutation(
  invalidation?: {
    investorId?: string
    pitchId?: string
    dataRoomToken?: string
  },
  callbacks?: MutationCallbacks<
    RequestInvestorEvaluationResponse,
    Options<RequestInvestorEvaluationData>
  >
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...requestInvestorEvaluationMutation(),
    onSuccess: async (data, variables, context) => {
      if (invalidation?.investorId && invalidation?.pitchId) {
        await queryClient.invalidateQueries({
          queryKey: getInvestorPitchDetailQueryKey({
            path: { id: invalidation.investorId, pitchId: invalidation.pitchId },
          }),
        })
      }
      if (invalidation?.dataRoomToken) {
        await queryClient.invalidateQueries({
          queryKey: getDataRoomContentQueryKey({
            path: { token: invalidation.dataRoomToken },
          }),
        })
      }
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// ============================================================================
// Startup Data Room Hooks
// ============================================================================

// Get startup data room
export function useStartupDataRoomQuery(
  startupId: string | undefined,
  queryOptions?: Partial<ReturnType<typeof getStartupDataRoomOptions>>
) {
  return useQuery({
    ...getStartupDataRoomOptions({ path: { startupId: startupId ?? '' } }),
    ...queryOptions,
    enabled: !!startupId && (queryOptions?.enabled ?? true),
  })
}

// Create startup data room
export function useCreateStartupDataRoomMutation(
  callbacks?: MutationCallbacks<CreateStartupDataRoomResponse, Options<CreateStartupDataRoomData>>,
  options?: Partial<Options<CreateStartupDataRoomData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...createStartupDataRoomMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: getStartupDataRoomQueryKey({
          path: { startupId: variables.path.startupId },
        }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Revoke startup data room
export function useRevokeStartupDataRoomMutation(
  callbacks?: MutationCallbacks<RevokeStartupDataRoomResponse, Options<RevokeStartupDataRoomData>>,
  options?: Partial<Options<RevokeStartupDataRoomData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...revokeStartupDataRoomMutation(options),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: getStartupDataRoomQueryKey({
          path: { startupId: variables.path.startupId },
        }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// ============================================================================
// Data Room Hooks (for investors viewing shared data rooms)
// ============================================================================

// Get data room content by token
export function useDataRoomContentQuery(
  token: string | undefined,
  queryOptions?: Partial<ReturnType<typeof getDataRoomContentOptions>>
) {
  return useQuery({
    ...getDataRoomContentOptions({ path: { token: token ?? '' } }),
    ...queryOptions,
    enabled: !!token && (queryOptions?.enabled ?? true),
  })
}

// Record data room view
export function useRecordDataRoomViewMutation(
  callbacks?: MutationCallbacks<RecordDataRoomViewResponse, Options<RecordDataRoomViewData>>,
  options?: Partial<Options<RecordDataRoomViewData>>
) {
  return useMutation({
    ...recordDataRoomViewMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Get data room deck download URL
export function useGetPitchDeckDownloadMutation(
  callbacks?: MutationCallbacks<GetPitchDeckDownloadResponse, Options<GetPitchDeckDownloadData>>,
  options?: Partial<Options<GetPitchDeckDownloadData>>
) {
  return useMutation({
    ...getPitchDeckDownloadMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Get data room document download URL
export function useGetDataRoomDocumentDownloadMutation(
  callbacks?: MutationCallbacks<
    GetDataRoomDocumentDownloadResponse,
    Options<GetDataRoomDocumentDownloadData>
  >,
  options?: Partial<Options<GetDataRoomDocumentDownloadData>>
) {
  return useMutation({
    ...getDataRoomDocumentDownloadMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Get data room video URL (generates presigned S3 URL on demand)
export function useGetDataRoomVideoUrlMutation(
  callbacks?: MutationCallbacks<GetDataRoomVideoUrlResponse, Options<GetDataRoomVideoUrlData>>,
  options?: Partial<Options<GetDataRoomVideoUrlData>>
) {
  return useMutation({
    ...getDataRoomVideoUrlMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Connect with startup via data room
export function useConnectWithStartupMutation(
  callbacks?: MutationCallbacks<ConnectWithStartupResponse, Options<ConnectWithStartupData>>,
  options?: Partial<Options<ConnectWithStartupData>>
) {
  return useMutation({
    ...connectWithStartupMutation(options),
    onSuccess: callbacks?.onSuccess,
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Get data room analytics (engagement tracking for founders)
export function useDataRoomAnalyticsQuery(
  token: string | undefined,
  queryOptions?: Partial<ReturnType<typeof getDataRoomAnalyticsOptions>>
) {
  return useQuery({
    ...getDataRoomAnalyticsOptions({ path: { token: token ?? '' } }),
    ...queryOptions,
    enabled: !!token && (queryOptions?.enabled ?? true),
  })
}

// ============================================================================
// Prescreen Hooks
// ============================================================================

// Admin: List prescreens
export function useAdminPrescreensQuery(
  options?: Options<AdminListPrescreensData>,
  queryOptions?: Partial<ReturnType<typeof adminListPrescreensOptions>>
) {
  return useQuery({
    ...adminListPrescreensOptions(options),
    ...queryOptions,
  })
}

// Admin: Get prescreen by ID
export function useAdminPrescreenByIdQuery(
  prescreenId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetPrescreenByIdOptions>>
) {
  return useQuery({
    ...adminGetPrescreenByIdOptions({ path: { id: prescreenId } }),
    ...queryOptions,
  })
}

// Admin: Get prescreen documents
export function useAdminPrescreenDocumentsQuery(
  prescreenId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetPrescreenDocumentsOptions>>
) {
  return useQuery({
    ...adminGetPrescreenDocumentsOptions({ path: { id: prescreenId } }),
    ...queryOptions,
  })
}

// Admin: Rerun prescreen
export function useAdminRerunPrescreenMutation(
  callbacks?: MutationCallbacks<AdminRerunPrescreenResponse, Options<AdminRerunPrescreenData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...adminRerunPrescreenMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: adminGetPrescreenByIdQueryKey({ path: { id: variables.path.id } }),
      })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Admin: Delete prescreen
export function useAdminDeletePrescreenMutation(
  callbacks?: MutationCallbacks<AdminDeletePrescreenResponse, Options<AdminDeletePrescreenData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...adminDeletePrescreenMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: adminListPrescreensQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Admin: Delete pitch session
export function useAdminDeletePitchSessionMutation(
  callbacks?: MutationCallbacks<
    AdminDeletePitchSessionResponse,
    Options<AdminDeletePitchSessionData>
  >
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...adminDeletePitchSessionMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: adminListEvaluationsQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Admin: Delete evaluation
export function useAdminDeleteEvaluationMutation(
  callbacks?: MutationCallbacks<AdminDeleteEvaluationResponse, Options<AdminDeleteEvaluationData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...adminDeleteEvaluationMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: adminListEvaluationsQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// Admin: Get eligible startups for prescreen
export function useAdminEligibleStartupsQuery(
  options?: Options<AdminGetEligibleStartupsData>,
  queryOptions?: Partial<ReturnType<typeof adminGetEligibleStartupsOptions>>
) {
  return useQuery({
    ...adminGetEligibleStartupsOptions(options),
    ...queryOptions,
  })
}

// Admin: Get startup documents
export function useAdminStartupDocumentsQuery(
  startupId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetStartupDocumentsOptions>>
) {
  return useQuery({
    ...adminGetStartupDocumentsOptions({ path: { startupId } }),
    ...queryOptions,
    enabled: !!startupId && (queryOptions?.enabled ?? true),
  })
}

// Admin: Trigger prescreen
export function useAdminTriggerPrescreenMutation(
  callbacks?: MutationCallbacks<AdminTriggerPrescreenResponse, Options<AdminTriggerPrescreenData>>
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...adminTriggerPrescreenMutation(),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: adminListPrescreensQueryKey() })
      callbacks?.onSuccess?.(data, variables, context)
    },
    onError: callbacks?.onError,
    onSettled: callbacks?.onSettled,
  })
}

// ============================================================================
// Onboarding Admin Hooks
// ============================================================================

// Admin: List onboarding chats
export function useAdminOnboardingQuery(
  options?: Options<AdminListOnboardingData>,
  queryOptions?: Partial<ReturnType<typeof adminListOnboardingOptions>>
) {
  return useQuery({
    ...adminListOnboardingOptions(options),
    ...queryOptions,
  })
}

// Admin: Get onboarding by ID
export function useAdminOnboardingByIdQuery(
  onboardingId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetOnboardingByIdOptions>>
) {
  return useQuery({
    ...adminGetOnboardingByIdOptions({ path: { id: onboardingId } }),
    ...queryOptions,
  })
}

// ============================================================================
// Data Room Admin Hooks
// ============================================================================

// Admin: List data rooms
export function useAdminDataRoomsQuery(
  options?: Options<AdminListDataRoomsData>,
  queryOptions?: Partial<ReturnType<typeof adminListDataRoomsOptions>>
) {
  return useQuery({
    ...adminListDataRoomsOptions(options),
    ...queryOptions,
  })
}

// Admin: Get data room by ID
export function useAdminDataRoomByIdQuery(
  dataRoomId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetDataRoomByIdOptions>>
) {
  return useQuery({
    ...adminGetDataRoomByIdOptions({ path: { id: dataRoomId } }),
    ...queryOptions,
  })
}

// Admin: Get data room analytics
export function useAdminDataRoomAnalyticsQuery(
  dataRoomId: string,
  queryOptions?: Partial<ReturnType<typeof adminGetDataRoomAnalyticsOptions>>
) {
  return useQuery({
    ...adminGetDataRoomAnalyticsOptions({ path: { id: dataRoomId } }),
    ...queryOptions,
  })
}

// Admin: List automated emails
export function useAdminAutomatedEmailsQuery(
  options?: Options<AdminListAutomatedEmailsData>,
  queryOptions?: Partial<ReturnType<typeof adminListAutomatedEmailsOptions>>
) {
  return useQuery({
    ...adminListAutomatedEmailsOptions(options),
    ...queryOptions,
  })
}

// Admin: Get automated email by ID
export function useAdminAutomatedEmailByIdQuery(
  id: string,
  queryOptions?: Partial<ReturnType<typeof adminGetAutomatedEmailByIdOptions>>
) {
  return useQuery({
    ...adminGetAutomatedEmailByIdOptions({ path: { id } }),
    ...queryOptions,
  })
}

// ============================================================================
// Public Hooks
// ============================================================================

// Get public investor by slug
export function usePublicInvestorQuery(
  slug: string | undefined,
  queryOptions?: Partial<ReturnType<typeof getPublicInvestorBySlugOptions>>
) {
  return useQuery({
    ...getPublicInvestorBySlugOptions({ path: { slug: slug ?? '' } }),
    ...queryOptions,
    enabled: !!slug && (queryOptions?.enabled ?? true),
  })
}
